import React, { createContext, useState, useContext, useEffect } from 'react';
import api from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe usarse dentro de AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Función para limpiar la sesión
  const clearSession = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete api.defaults.headers.common['Authorization'];
    setUser(null);
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    if (token && userData) {
      try {
        const parsedUser = JSON.parse(userData);
        if (parsedUser && typeof parsedUser === 'object') {
          setUser(parsedUser);
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        } else {
          // Si el formato no es válido, limpiar localStorage
          clearSession();
        }
      } catch (error) {
        console.error('Error al parsear datos de usuario:', error);
        // Si hay error al parsear, limpiar localStorage
        clearSession();
      }
    } else {
      // Si no hay token o userData, asegurarse de que el estado esté limpio
      clearSession();
    }
    setLoading(false);
  }, []);

  // Escuchar cambios en localStorage para detectar cuando se limpia la sesión desde otro lugar
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'token' && !e.newValue) {
        // El token fue eliminado, limpiar el estado
        setUser(null);
        delete api.defaults.headers.common['Authorization'];
      }
      if (e.key === 'user' && !e.newValue) {
        // El usuario fue eliminado, limpiar el estado
        setUser(null);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    // También escuchar eventos personalizados para cambios en la misma pestaña
    const handleCustomStorageChange = () => {
      const token = localStorage.getItem('token');
      const userData = localStorage.getItem('user');
      if (!token || !userData) {
        setUser(null);
        delete api.defaults.headers.common['Authorization'];
      }
    };

    window.addEventListener('clearSession', handleCustomStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('clearSession', handleCustomStorageChange);
    };
  }, []);

  const login = async (email, password) => {
    try {
      const response = await api.post('/auth/login', { email, password });
      
      // Verificar que la respuesta tenga el formato esperado
      if (!response.data || !response.data.token || !response.data.user) {
        return {
          success: false,
          message: 'Respuesta del servidor inválida'
        };
      }
      
      const { token, user } = response.data;
      
      // Validar que user sea un objeto válido
      if (!user || typeof user !== 'object') {
        return {
          success: false,
          message: 'Datos de usuario inválidos'
        };
      }
      
      // Guardar en localStorage de forma segura
      try {
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(user));
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        setUser(user);
        
        // Esperar un momento para asegurar que el token esté completamente configurado
        // antes de permitir que otros componentes hagan peticiones
        await new Promise(resolve => setTimeout(resolve, 100));
      } catch (storageError) {
        console.error('Error al guardar en localStorage:', storageError);
        return {
          success: false,
          message: 'Error al guardar datos de sesión'
        };
      }
      
      return { success: true };
    } catch (error) {
      console.error('Error en login:', error);
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Error al iniciar sesión'
      };
    }
  };

  const logout = () => {
    clearSession();
    // Disparar evento personalizado para notificar a otros componentes
    window.dispatchEvent(new Event('clearSession'));
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

